import { NetworkError, normalizeApiError } from "./errors";

/**
 * SpiffArena backend への HTTP 境界。
 *
 * ベースパスは相対（既定 `/v1.0`）。本番は CloudFront が `/v1.0/*` を ALB へ
 * 転送する同一オリジン構成、dev は vite proxy が同形を再現するため、絶対 URL を
 * 使わない（backend に CORS 許可設定がない）。
 *
 * 依存は注入で受ける。モジュールスコープのシングルトンにしないのは、
 * テストで独立したクライアントを差せるようにするため。
 */

export type RequestOptions = {
  /** URL に載せるクエリ。undefined / null の値は省略される。 */
  readonly query?: Readonly<
    Record<string, string | number | boolean | null | undefined>
  >;
  readonly signal?: AbortSignal;
  readonly headers?: Readonly<Record<string, string>>;
};

export type ApiClient = {
  get<T>(_path: string, _options?: RequestOptions): Promise<T>;
  post<T>(
    _path: string,
    _body?: unknown,
    _options?: RequestOptions,
  ): Promise<T>;
  put<T>(_path: string, _body?: unknown, _options?: RequestOptions): Promise<T>;
  del<T>(_path: string, _options?: RequestOptions): Promise<T>;
};

export type CreateApiClientDeps = {
  /** 例: "/v1.0" */
  readonly basePath: string;
  /** リクエストごとに呼ばれる。backend が cookie を更新するため毎回読み直す必要がある。 */
  readonly getAuthHeaders: () => Record<string, string>;
  /** 401 を受けたときのセッション破棄＋再ログイン。 */
  readonly onUnauthorized: () => void;
  /** テスト用の差し替え。 */
  readonly fetchImpl?: typeof fetch;
};

export function createApiClient(deps: CreateApiClientDeps): ApiClient {
  const fetchImpl = deps.fetchImpl ?? globalThis.fetch;

  async function request<T>(
    method: string,
    path: string,
    body: unknown,
    options: RequestOptions | undefined,
  ): Promise<T> {
    const headers = new Headers(deps.getAuthHeaders());
    for (const [name, value] of Object.entries(options?.headers ?? {})) {
      headers.set(name, value);
    }

    // FormData は boundary 付きの Content-Type を fetch に決めさせる必要がある
    const isFormData = body instanceof FormData;
    if (body !== undefined && !isFormData) {
      headers.set("Content-Type", "application/json");
    }

    let response: Response;
    try {
      response = await fetchImpl(
        buildUrl(deps.basePath, path, options?.query),
        {
          method,
          headers,
          // 同一オリジンでは冗長だが、別オリジン構成でも壊れないよう常に付ける
          credentials: "include",
          body: serializeBody(body, isFormData),
          signal: options?.signal,
        },
      );
    } catch (cause) {
      throw new NetworkError(
        cause instanceof Error ? cause.message : "ネットワークエラー",
        cause,
      );
    }

    if (!response.ok) {
      const error = normalizeApiError(
        response.status,
        response.statusText,
        await response.text().catch(() => ""),
      );
      // 401 は呼び出し側の catch に頼らず必ず再ログインを起動する
      if (response.status === 401) deps.onUnauthorized();
      throw error;
    }

    return (await parseBody(response)) as T;
  }

  return {
    get: (path, options) => request("GET", path, undefined, options),
    post: (path, body, options) => request("POST", path, body, options),
    put: (path, body, options) => request("PUT", path, body, options),
    del: (path, options) => request("DELETE", path, undefined, options),
  };
}

function buildUrl(
  basePath: string,
  path: string,
  query: RequestOptions["query"],
): string {
  const url = `${basePath}${path}`;
  if (query === undefined) return url;

  const params = new URLSearchParams();
  for (const [name, value] of Object.entries(query)) {
    if (value === undefined || value === null) continue;
    params.set(name, String(value));
  }

  const queryString = params.toString();
  return queryString === "" ? url : `${url}?${queryString}`;
}

function serializeBody(
  body: unknown,
  isFormData: boolean,
): BodyInit | undefined {
  if (body === undefined) return undefined;
  if (isFormData) return body as FormData;
  return JSON.stringify(body);
}

async function parseBody(response: Response): Promise<unknown> {
  if (response.status === 204) return null;

  const text = await response.text();
  if (text === "") return null;

  try {
    return JSON.parse(text);
  } catch {
    // JSON でない 2xx（BPMN XML のダウンロード等）は生文字列を返す
    return text;
  }
}
